/**
 * Enhanced Conversions utility module for Google Ads.
 * Normalizes user data according to Google's specifications.
 * @see https://support.google.com/google-ads/answer/13258081
 */

import { captureException } from "@sentry/browser";
import {
  normalizeEmail,
  normalizePhoneNumber,
  normalizeAddress,
  normalizeCountryNameToCode,
} from "./dataNormalizers";
import { hasMarketingConsent } from "./cookiebotConsent";

/**
 * Normalizes address for Google Ads Enhanced Conversions.
 * Uses the general normalizeAddress utility and adds Google-specific country code conversion.
 * @param {object} addressData - The address data object.
 * @returns {object|null} The normalized address object or null if no valid data.
 */
function normalizeAddressForGoogle(addressData) {
  // Use general normalizer first
  const normalized = normalizeAddress(addressData);
  if (!normalized) return null;

  // Convert country to country code for Google Ads
  if (normalized.country) {
    normalized.country = normalizeCountryNameToCode(normalized.country);
  }

  return normalized;
}

/**
 * Normalizes and validates an array of values.
 * @param {string|string[]} values - Single value or array of values.
 * @param {Function} normalizer - Function to normalize each value.
 * @param {number} maxValues - Maximum number of values to return.
 * @returns {string|string[]|null} Normalized value(s) or null if no valid data.
 */
function normalizeMultipleValues(values, normalizer, maxValues) {
  if (!values) return null;

  // Handle single value
  if (!Array.isArray(values)) {
    const normalized = normalizer(values);
    return normalized || null;
  }

  // Handle array of values
  const normalizedValues = values
    .slice(0, maxValues) // Limit to maxValues
    .map((value) => normalizer(value))
    .filter((value) => value !== null);

  if (normalizedValues.length === 0) return null;
  if (normalizedValues.length === 1) return normalizedValues[0];
  return normalizedValues;
}

/**
 * Internal implementation of enhanced conversions data processing.
 * @private
 * @param {string|string[]} emails - Email address(es).
 * @param {string|string[]} phones - Phone number(s).
 * @param {object|object[]} addresses - Address object(s) - plain objects with address fields.
 * @returns {object|null} The enhanced conversions object or null if no valid data.
 */
function processEnhancedConversionsData(emails, phones, addresses) {
  const enhancedData = {};

  // Process emails (up to 3)
  if (emails) {
    const normalizedEmails = normalizeMultipleValues(emails, normalizeEmail, 3);
    if (normalizedEmails) enhancedData.email = normalizedEmails;
  }

  // Process phones (up to 3)
  if (phones) {
    const normalizedPhones = normalizeMultipleValues(
      phones,
      normalizePhoneNumber,
      3,
    );
    if (normalizedPhones) enhancedData.phone_number = normalizedPhones;
  }

  // Process addresses (up to 2)
  if (addresses) {
    if (!Array.isArray(addresses)) {
      // Single address
      const normalizedAddress = normalizeAddressForGoogle(addresses);
      if (normalizedAddress) enhancedData.address = normalizedAddress;
    } else {
      // Array of addresses
      const normalizedAddresses = addresses
        .slice(0, 2) // Limit to 2 addresses
        .map((addr) => normalizeAddressForGoogle(addr))
        .filter((addr) => addr !== null);

      if (normalizedAddresses.length === 1) {
        enhancedData.address = normalizedAddresses[0];
      } else if (normalizedAddresses.length > 1) {
        enhancedData.address = normalizedAddresses;
      }
    }
  }

  // Return null if no valid data
  return Object.keys(enhancedData).length > 0 ? enhancedData : null;
}

/**
 * Creates the enhanced conversions data object for Google Ads, supporting multiple values for email (up to 3) and phone (up to 3).
 * @param {string|string[]} emails - Email address(es).
 * @param {string|string[]} phones - Phone number(s).
 * @param {object|object[]} addresses - Address object(s) - plain objects with address fields.
 * @returns {object|null} The enhanced conversions object or null if no consent or no valid data.
 */
export function createEnhancedConversionsData(emails, phones, addresses) {
  // Check consent first
  if (!hasMarketingConsent()) {
    return null;
  }

  try {
    return processEnhancedConversionsData(emails, phones, addresses);
  } catch (error) {
    // Log to Sentry with context (NO PII)
    captureException(error, (scope) => {
      scope.setTag("enhanced_conversions", "processing_error");
      scope.setLevel("error");
      scope.setContext("enhanced_conversions_metadata", {
        has_emails: !!emails,
        has_phones: !!phones,
        has_addresses: !!addresses,
        emails_type: Array.isArray(emails) ? "array" : typeof emails,
        phones_type: Array.isArray(phones) ? "array" : typeof phones,
        addresses_type: Array.isArray(addresses) ? "array" : typeof addresses,
        emails_count: Array.isArray(emails) ? emails.length : emails ? 1 : 0,
        phones_count: Array.isArray(phones) ? phones.length : phones ? 1 : 0,
        addresses_count: Array.isArray(addresses)
          ? addresses.length
          : addresses
            ? 1
            : 0,
      });
      return scope;
    });

    return null;
  }
}

/**
 * Merges base user data with form inputs, handling arrays and avoiding duplicates.
 * @param {object|null} baseUserData - The existing user data (from logged-in user).
 * @param {string} formEmail - The email from form input (required).
 * @param {string|null} formPhone - The phone from form input (optional).
 * @returns {object|null} The merged user data object or null if no valid data.
 */
export function mergeUserDataWithFormInputs(
  baseUserData,
  formEmail,
  formPhone = null,
) {
  // Normalize form inputs
  const normalizedFormEmail = normalizeEmail(formEmail);
  const normalizedFormPhone = formPhone
    ? normalizePhoneNumber(formPhone)
    : null;

  // If no valid form inputs, return base data as-is
  if (!normalizedFormEmail && !normalizedFormPhone) {
    return baseUserData;
  }

  // If no base data, create new data from form inputs
  if (!baseUserData) {
    if (!normalizedFormEmail) return null;

    const newData = { email: normalizedFormEmail };
    if (normalizedFormPhone) {
      newData.phone_number = normalizedFormPhone;
    }
    return newData;
  }

  // Start with a copy of base data
  const mergedData = { ...baseUserData };

  // Merge email if provided
  if (normalizedFormEmail) {
    if (!baseUserData.email) {
      // No existing email, just add the form email
      mergedData.email = normalizedFormEmail;
    } else if (Array.isArray(baseUserData.email)) {
      // Base has array of emails
      const normalizedExisting = baseUserData.email.map((e) =>
        normalizeEmail(e),
      );
      if (!normalizedExisting.includes(normalizedFormEmail)) {
        mergedData.email = [...baseUserData.email, normalizedFormEmail];
      }
    } else {
      // Base has single email string
      const normalizedBase = normalizeEmail(baseUserData.email);
      if (normalizedBase !== normalizedFormEmail) {
        mergedData.email = [baseUserData.email, normalizedFormEmail];
      } else {
        // Same email but possibly different case - use normalized version
        mergedData.email = normalizedFormEmail;
      }
    }
  }

  // Merge phone if provided
  if (normalizedFormPhone) {
    if (!baseUserData.phone_number) {
      // No existing phone, just add the form phone
      mergedData.phone_number = normalizedFormPhone;
    } else if (Array.isArray(baseUserData.phone_number)) {
      // Base has array of phones
      if (!baseUserData.phone_number.includes(normalizedFormPhone)) {
        mergedData.phone_number = [
          ...baseUserData.phone_number,
          normalizedFormPhone,
        ];
      }
    } else {
      // Base has single phone string
      if (baseUserData.phone_number !== normalizedFormPhone) {
        mergedData.phone_number = [
          baseUserData.phone_number,
          normalizedFormPhone,
        ];
      }
    }
  }

  return mergedData;
}
